import React from 'react';
import { Input, Button, message, Modal, Divider, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useSelector, useDispatch } from 'react-redux';
import { useAsyncCallback } from 'react-async-hook';
import toLower from 'lodash/toLower';
import { actions, selectActiveRole } from 'features/auth';
import userService from 'services/users.service';
import { selectors } from 'features/auth';
import { passwordValidator } from 'utils';
import Form from 'components/Form';
import FormField from 'components/Authentication/FormField';
import Space, { Spacer } from 'components/Space/Space';
import { ROLES } from 'constants';

export default function ChangeUserName({ setVisible }) {
  const currentUser = useSelector(selectors.selectUser);
  const activeRole = useSelector(selectActiveRole);
  const isInternalUser = ![ROLES.FAMILY, ROLES.PROVIDER].includes(activeRole);
  const dispatch = useDispatch();
  const history = useHistory();
  const [isConfirmModalVisible, setIsConfirmModalVisible] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState(null);
  const [confirmForm] = Form.useForm();

  const onConfirmation = () => {
    dispatch(actions.logout.pending());
    history.push('/');
  };

  const changeUsername = useAsyncCallback(async (values) => {
    return Auth.signIn(currentUser.email, values.password)
      .then(() =>
        userService
          .changeUserName({ currentEmail: currentUser.email, newEmail: toLower(values.newEmail) })
          .then(async () => {
            setIsConfirmModalVisible(false);

            Modal.success({
              title: 'Please check your email',
              content: (
                <div>We've sent a temporary password to your email address. Please re-login using that password.</div>
              ),
              onOk: () => onConfirmation(),
              onCancel: () => onConfirmation(),
            });
          })
          .catch((err) => {
            console.log('Error updating email', err);
            message.error(err?.apierror?.message || 'Something went wrong.');
          }),
      )
      .catch((err) => {
        message.error('Password is incorrect.');
      });
  });

  const handleSubmit = (values) => {
    setPendingValues(values);
    confirmForm.resetFields();
    setIsConfirmModalVisible(true);
  };

  const handleConfirm = async () => {
    try {
      const values = await confirmForm.validateFields();
      changeUsername.execute({ ...pendingValues, password: values.password });
    } catch (error) {
      // Form validation failed, errors will be displayed by the form
    }
  };

  if (isInternalUser) {
    return <div className="m-7">Internal users cannot change their email address</div>;
  }

  return (
    <>
      <Form onFinish={handleSubmit} data-testid="change-username-form" layout="vertical">
        <div className="m-7 mb-12">
          <FormField label="Current email address" className="max-w-[800px]">
            <Input disabled value={currentUser?.email} />
          </FormField>

          <FormField
            label="New email address"
            name="newEmail"
            rules={[
              { required: true, message: 'Email address is required' },
              { type: 'email', message: 'Email address is invalid' },
              () => ({
                validator(rule, value) {
                  if (value && toLower(value) === toLower(currentUser?.email)) {
                    return Promise.reject('New email address cannot be the same as the current one');
                  } else return Promise.resolve();
                },
              }),
            ]}
            className="max-w-[800px]"
          >
            <Input placeholder="New email address" />
          </FormField>
        </div>

        <Divider className="mb-0" />

        <Space className="my-4 mx-8">
          <Spacer />

          <Button loading={changeUsername.loading} disabled={changeUsername.loading} type="primary" htmlType="submit">
            Submit
          </Button>
        </Space>
      </Form>

      <Modal
        title="Confirm Password"
        open={isConfirmModalVisible}
        onCancel={() => setIsConfirmModalVisible(false)}
        footer={null}
      >
        <Form form={confirmForm} layout="vertical">
          <Typography.Text type="secondary" className="block mb-2">
            <span className="font-semibold">Please enter your password</span> to confirm this change:
          </Typography.Text>

          <FormField name="password" rules={[{ required: true, message: 'Password is required' }, passwordValidator]}>
            <Input.Password placeholder="Password" onPressEnter={handleConfirm} />
          </FormField>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsConfirmModalVisible(false)}>Cancel</Button>
            <Button type="primary" loading={changeUsername.loading} onClick={handleConfirm}>
              Confirm
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
